import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { fetchUserID, updateUser } from "../../utilities";
import { useDispatch } from "react-redux";
import { login } from "../../features/userSlice";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";

import { callMsGraph } from "../../utils/MsGraphApiCall";

// Msal imports
import { useMsal } from "@azure/msal-react";
import {
  InteractionStatus,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { loginRequest } from "../../authConfig.js";
import { fetchUser } from "../../views/Admin/Utilities/api";
import { consumePostLoginRedirect } from "../../utils/postLoginRedirect";
import { useIsDarkMode } from "../../theme/ThemeContext";

const InitUser = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { instance, inProgress } = useMsal();
  const isDark = useIsDarkMode();

  const [progress, setProgress] = useState(1);
  const [initError, setInitError] = useState(null);
  const [debugEmail, setDebugEmail] = useState("");
  const didInit = useRef(false);

  useEffect(() => {
    if (didInit.current || inProgress !== InteractionStatus.None) return;
    didInit.current = true;

    const init = async () => {
      const finish = (user) => {
        dispatch(login(user));
        setProgress(100);
        // Return to the page the user originally requested (stored before
        // the auth bounce) rather than always dumping them on the homepage.
        navigate(consumePostLoginRedirect() || "/", { replace: true });
      };

      try {
        const graph = await callMsGraph();
        setProgress(20);

        // Accounts without a mailbox get a minimal profile.
        if (!graph.mail) {
          finish({
            name: graph.displayName,
            email: graph.userPrincipalName,
            id: graph.id,
            mobile: graph.mobilePhone,
            title: graph.jobTitle,
            userID: "1",
            roles: [],
            branch: graph.displayName.split(" ")[0],
          });
          return;
        }

        setDebugEmail(graph.mail);

        // Directory record (branch/rep code) and app user record (roles)
        // are independent — fetch them together instead of the old
        // effect-per-setter waterfall that raced and could navigate away
        // without ever dispatching the login.
        const [directory, userRecord] = await Promise.all([
          fetchUserID(graph.mail),
          fetchUser(graph.id),
        ]);
        setProgress(75);

        if (userRecord?.deactivated) {
          setInitError(
            "This account has been deactivated. Contact HelpDesk if you believe this is a mistake."
          );
          return;
        }

        if (!directory) {
          // No directory match: the old flow silently spun forever here.
          setInitError(
            "We couldn't find your account details. Refresh to retry, or contact HelpDesk below."
          );
          return;
        }

        const user = {
          name: graph.displayName,
          email: graph.mail,
          id: graph.id,
          mobile: graph.mobilePhone,
          title: graph.jobTitle,
          userID: directory.UserID,
          roles: userRecord?.roles ?? [],
          // A branch set in the User Manager wins over the directory's.
          branch: userRecord?.branch_override || directory.Branch,
          salesRepCode: directory.SalesRepCode,
        };

        // Server-side profile sync — no reason to block navigation on it.
        // It always carries the directory's branch; the server keeps any
        // admin override on top (server/routes/user/setUser.js).
        updateUser({ ...user, branch: directory.Branch }).catch(() => {});
        finish(user);
      } catch (e) {
        if (e instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect({
            ...loginRequest,
            account: instance.getActiveAccount(),
          });
        } else {
          console.error("InitUser:", e);
          setInitError(
            "Something went wrong while signing you in. Refresh to retry, or contact HelpDesk below."
          );
        }
      }
    };

    init();
  }, [inProgress, instance, dispatch, navigate]);

  const formattedBody = `Debug Details \n ${debugEmail || "unknown user"} \n ${
    initError || "no error"
  }`;

  const pct = Math.max(0, Math.min(100, Math.round(progress)));

  // Calm, branded holding screen everyone sees while sign-in finishes.
  return (
    <div className="app-page relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-4 py-12">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-1/3 h-[28rem] w-[28rem] -translate-x-[70%] -translate-y-1/2 rounded-full bg-sky-400/20 blur-3xl dark:bg-sky-500/15" />
        <div className="absolute left-1/2 top-1/2 h-[24rem] w-[24rem] -translate-x-[20%] -translate-y-[20%] rounded-full bg-violet-400/20 blur-3xl dark:bg-violet-500/15" />
      </div>

      <div className="app-card relative w-full max-w-md rounded-[2rem] border px-8 py-10 text-center">
        <img
          src={
            isDark
              ? require("../../assets/NodeLogo2.png")
              : require("../../assets/NodeLogoDark.png")
          }
          alt="Big C Node"
          className="mx-auto h-auto w-[150px]"
        />
        <p className="mt-6 text-[0.95rem] leading-relaxed">
          Welcome to Node! Please wait while we authenticate you.
        </p>

        {initError ? (
          <p
            role="alert"
            className="mt-6 rounded-xl bg-rose-500/10 px-4 py-3 text-sm leading-relaxed text-rose-700 dark:text-rose-300"
          >
            {initError}
          </p>
        ) : (
          <div className="mt-8">
            <div
              role="progressbar"
              aria-label="Signing you in"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
              className="h-1.5 w-full overflow-hidden rounded-full bg-[color:var(--app-hover)]"
            >
              <div
                className="h-full rounded-full bg-gradient-to-r from-sky-500 to-violet-500 transition-[width] duration-700 ease-out"
                style={{ width: `${Math.max(pct, 4)}%` }}
              />
            </div>
            <p className="app-muted mt-2 text-xs tabular-nums">{`${pct}%`}</p>
          </div>
        )}
      </div>

      <div className="relative mt-8 flex flex-col items-center gap-3 text-center">
        <p className="app-muted text-sm">Stuck on this page? Contact HelpDesk!</p>
        <a
          className="app-btn app-btn-secondary"
          href={`mailto:helpdesk@bigclumber.com?subject=${encodeURIComponent(
            "Node Help | Stuck on INIT USER"
          )}&body=${encodeURIComponent(formattedBody)}`}
        >
          Submit a Ticket
          <ArrowOutwardIcon sx={{ fontSize: 17 }} />
        </a>
      </div>
    </div>
  );
};

export default InitUser;
